import { useState, useEffect, useCallback, useRef } from 'react';
import { exclusiveServiceAPI, uploadAPI, BASE_URL } from '../services/api';

const s = {
  container: { width: '100%' },
  header: { marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  title: { fontSize: '24px', fontWeight: '700', color: '#1e293b', margin: 0 },
  subtitle: { fontSize: '13px', color: '#64748b', marginTop: '4px' },
  card: { backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' },
  toolbar: { display: 'flex', justifyContent: 'flex-end', padding: '16px', borderBottom: '1px solid #e2e8f0' },
  addBtn: { padding: '10px 20px', backgroundColor: '#4f46e5', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
  td: { padding: '16px', borderBottom: '1px solid #f1f5f9', fontSize: '14px', color: '#1e293b', verticalAlign: 'middle' },
  thumb: { width: '40px', height: '40px', objectFit: 'contain', borderRadius: '8px', border: '1px solid #e2e8f0', padding: '4px' },
  badge: { padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', cursor: 'pointer' },
  active: { backgroundColor: '#dcfce7', color: '#166534' },
  inactive: { backgroundColor: '#fee2e2', color: '#991b1b' },
  iconBtn: { padding: '8px', border: 'none', borderRadius: '6px', backgroundColor: 'transparent', cursor: 'pointer', color: '#64748b' },
  modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modalBox: { backgroundColor: '#fff', borderRadius: '16px', width: '90%', maxWidth: '500px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
  modalHead: { padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  modalBody: { padding: '24px', overflowY: 'auto', flex: 1 },
  modalFoot: { padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '12px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '500', color: '#475569', marginBottom: '6px' },
  input: { width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' },
  btn: { padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', border: 'none' },
  primary: { backgroundColor: '#4f46e5', color: '#fff' },
  secondary: { backgroundColor: '#f1f5f9', color: '#475569' },
  error: { padding: '12px 16px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', marginBottom: '16px', fontSize: '14px' },
  empty: { textAlign: 'center', padding: '60px 20px', color: '#64748b' },
};

const ExclusiveServices = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', icon: '', link: '', sortOrder: 0, isActive: true });
  const [formErr, setFormErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const fetch = useCallback(async () => {
    try { setLoading(true); const r = await exclusiveServiceAPI.getAll(); setItems(r.data.data || []); }
    catch { setError('Failed to fetch'); } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const openCreate = () => { setEditing(null); setForm({ title: '', icon: '', link: '', sortOrder: items.length + 1, isActive: true }); setFormErr(''); setShowModal(true); };
  const openEdit = (item) => { setEditing(item); setForm({ title: item.title, icon: item.icon || '', link: item.link, sortOrder: item.sortOrder, isActive: item.isActive }); setFormErr(''); setShowModal(true); };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try { const r = await uploadAPI.uploadTipImage(file); if (r.data?.url) setForm(p => ({ ...p, icon: r.data.url })); }
    catch { setFormErr('Upload failed'); } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const handleSubmit = async () => {
    if (!form.title || !form.link) { setFormErr('Title and link are required'); return; }
    try { setSaving(true); editing ? await exclusiveServiceAPI.update(editing.id, form) : await exclusiveServiceAPI.create(form); setShowModal(false); fetch(); }
    catch (e) { setFormErr(e.response?.data?.error || 'Failed'); } finally { setSaving(false); }
  };

  const imgUrl = (p) => p?.startsWith('http') ? p : `${BASE_URL}${p}`;

  return (
    <div style={s.container}>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Exclusive Services</h1>
          <p style={s.subtitle}>External service links shown on the homepage</p>
        </div>
      </div>
      {error && <div style={s.error}>{error}</div>}
      <div style={s.card}>
        <div style={s.toolbar}>
          <button style={s.addBtn} onClick={openCreate}>+ Add Service</button>
        </div>
        {loading ? <div style={s.empty}>Loading...</div> : items.length === 0 ? <div style={s.empty}>No services yet</div> : (
          <table style={s.table}>
            <thead><tr>
              <th style={{ ...s.th, width: '60px' }}>Order</th>
              <th style={{ ...s.th, width: '60px' }}>Icon</th>
              <th style={s.th}>Title</th>
              <th style={s.th}>Link</th>
              <th style={{ ...s.th, width: '100px' }}>Status</th>
              <th style={{ ...s.th, width: '100px' }}>Actions</th>
            </tr></thead>
            <tbody>{items.map(item => (
              <tr key={item.id}>
                <td style={s.td}>{item.sortOrder}</td>
                <td style={s.td}>{item.icon ? <img src={imgUrl(item.icon)} alt="" style={s.thumb} /> : '-'}</td>
                <td style={{ ...s.td, fontWeight: '600' }}>{item.title}</td>
                <td style={s.td}><a href={item.link} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', fontSize: '13px', wordBreak: 'break-all' }}>{item.link}</a></td>
                <td style={s.td}><span style={{ ...s.badge, ...(item.isActive ? s.active : s.inactive) }} onClick={async () => { await exclusiveServiceAPI.toggleActive(item.id); fetch(); }}>{item.isActive ? 'Active' : 'Inactive'}</span></td>
                <td style={s.td}>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button style={s.iconBtn} onClick={() => openEdit(item)}><svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg></button>
                    <button style={{ ...s.iconBtn, color: '#dc2626' }} onClick={async () => { if (window.confirm('Delete?')) { await exclusiveServiceAPI.delete(item.id); fetch(); } }}><svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg></button>
                  </div>
                </td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div style={s.modal} onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div style={s.modalBox}>
            <div style={s.modalHead}>
              <span style={{ fontSize: '18px', fontWeight: '600' }}>{editing ? 'Edit Service' : 'Add Service'}</span>
              <button style={s.iconBtn} onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <div style={s.modalBody}>
              {formErr && <div style={s.error}>{formErr}</div>}
              <div style={{ marginBottom: '16px' }}>
                <label style={s.label}>Title *</label>
                <input style={s.input} value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. Buy Used Car" />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={s.label}>Link *</label>
                <input style={s.input} value={form.link} onChange={e => setForm({ ...form, link: e.target.value })} placeholder="https://vehicleinfo.app/sell-used-car" />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={s.label}>Icon</label>
                {form.icon ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                    <img src={imgUrl(form.icon)} alt="" style={{ width: '40px', height: '40px', objectFit: 'contain' }} />
                    <span style={{ flex: 1, fontSize: '12px', color: '#64748b', wordBreak: 'break-all' }}>{form.icon}</span>
                    <button style={{ ...s.btn, ...s.secondary, padding: '4px 10px', fontSize: '12px' }} onClick={() => setForm({ ...form, icon: '' })}>Remove</button>
                  </div>
                ) : (
                  <div><input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} disabled={uploading} style={{ fontSize: '13px' }} />{uploading && <span style={{ fontSize: '12px', color: '#64748b' }}> Uploading...</span>}</div>
                )}
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <label style={s.label}>Sort Order</label>
                  <input style={s.input} type="number" value={form.sortOrder} onChange={e => setForm({ ...form, sortOrder: Number(e.target.value) })} />
                </div>
                <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', paddingBottom: '4px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} />
                    <span style={{ fontSize: '14px' }}>Active</span>
                  </label>
                </div>
              </div>
            </div>
            <div style={s.modalFoot}>
              <button style={{ ...s.btn, ...s.secondary }} onClick={() => setShowModal(false)}>Cancel</button>
              <button style={{ ...s.btn, ...s.primary, opacity: saving ? 0.6 : 1 }} onClick={handleSubmit} disabled={saving}>{saving ? 'Saving...' : editing ? 'Update' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExclusiveServices;
